import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Search, Lock, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { makeApi } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";
import { complianceProgress } from "@/lib/progress";
import ProgressCell from "@/components/ProgressCell";
import NudgeButton from "@/components/NudgeButton";
import ReferralSettings from "@/components/ReferralSettings";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import RevenueSummary from "@/components/revenue/RevenueSummary";
import SuggestionsCard from "@/components/suggestions/SuggestionsCard";

const address = (c) =>
  [c.address_street, c.address_city, [c.address_state, c.address_zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

export default function Admin() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  const [needsHelpFirst, setNeedsHelpFirst] = useState(false);
  const { startPreview } = useRole();
  const [starting, setStarting] = useState(false);

  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    api.get("/admin/revenue").then(({ data }) => setRevenue(data)).catch(() => setRevenue("error"));
  }, [api]);

  const preview = async () => {
    setStarting(true);
    try {
      await startPreview();
    } catch {
      toast.error("Could not start preview. Try again.");
      setStarting(false);
    }
  };

  useEffect(() => {
    api.get("/admin/customers").then(({ data }) => setData(data))
      .catch((e) => (e.response?.status === 403 ? setDenied(true) : setFailed(true)));
  }, [api]);

  if (denied) {
    return (
      <Shell>
        <div className="max-w-md mx-auto brutal-border bg-[#141414] p-10 text-center" data-testid="admin-denied">
          <Lock size={36} className="mx-auto mb-4 text-muted-foreground" />
          <h1 className="font-heading font-black text-2xl">Owner access only.</h1>
        </div>
      </Shell>
    );
  }
  if (failed) return <Shell><div className="text-sm text-muted-foreground" data-testid="admin-error">Could not load customers. Refresh to try again.</div></Shell>;
  if (!data) return <Shell><div className="font-mono-label text-xs text-muted-foreground">LOADING…</div></Shell>;

  const term = q.toLowerCase();
  const rows = data.customers
    .filter((c) => [c.email, c.name, c.legal_name, c.dba_name, c.phone, address(c)].join(" ").toLowerCase().includes(term))
    .map((c) => ({ ...c, ...complianceProgress(c, c.items || []) }));
  if (needsHelpFirst) rows.sort((a, b) => a.pct - b.pct);
  const onSent = (uid, at) => setData((d) => ({ ...d, customers: d.customers.map((x) => (x.user_id === uid ? { ...x, last_reminded_at: at } : x)) }));
  const fil = (c) => `${c.parts[0].done}/${c.parts[0].total}`;

  return (
    <Shell>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono-label text-xs text-[#FFD600] mb-2">OWNER · CUSTOMERS</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl leading-none" data-testid="admin-title">
            {data.total} customers
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
        <button
          onClick={preview}
          disabled={starting}
          data-testid="preview-as-client-button"
          className="min-h-[44px] cta-primary px-4 py-2 font-mono-label text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50 shrink-0"
        >
          {starting ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview as Client
        </button>
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="w-full min-h-[44px] bg-[#141414] border border-[#262626] pl-9 pr-3 py-2 text-base sm:text-sm focus:outline-none focus:border-white"
          />
        </div>
        </div>
      </div>
      {revenue === "error" ? (
        <div className="text-sm text-[#F87171] mb-8" data-testid="revenue-summary-error">Could not load revenue from Stripe. Open the Revenue page to retry.</div>
      ) : (
        <RevenueSummary data={revenue} compact />
      )}
      <SuggestionsCard api={api} />
      <ReferralSettings api={api} />
      <ReferralLeaderboard api={api} />
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-xs text-muted-foreground" data-testid="admin-needs-help-count">
          {rows.filter((r) => r.pct < 50).length} need help (under 50%)
        </div>
        <button onClick={() => setNeedsHelpFirst((v) => !v)} data-testid="admin-sort-needs-help" aria-pressed={needsHelpFirst}
          className={`min-h-[40px] px-3 font-mono-label text-[11px] border transition-colors ${needsHelpFirst ? "border-[#FFD600] text-[#FFD600]" : "border-[#262626] hover:border-white"}`}>
          {needsHelpFirst ? "Sorted: least complete first" : "Sort: least complete first"}
        </button>
      </div>
      <div className="md:hidden space-y-3" data-testid="admin-customers-cards">
        {rows.map((c) => (
          <div key={c.user_id} className="brutal-border bg-[#141414] p-4 text-sm space-y-1.5" data-testid={`admin-card-${c.user_id}`}>
            <div className="flex justify-between gap-3">
              <div className="font-heading font-bold text-base break-words">{c.legal_name || c.name || "—"}</div>
              <ProgressCell pct={c.pct} tid={`admin-card-progress-${c.user_id}`} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground" data-testid={`admin-card-filings-${c.user_id}`}>Filings {fil(c)}</div>
              <NudgeButton c={c} api={api} onSent={onSent} tid={`admin-card-nudge-${c.user_id}`} />
            </div>
            {c.name && c.legal_name && <div className="text-muted-foreground">{c.name}</div>}
            {c.email && <a href={`mailto:${c.email}`} className="block underline break-all min-h-[32px] leading-8">{c.email}</a>}
            {c.phone && <a href={`tel:${c.phone}`} className="block underline min-h-[32px] leading-8">{c.phone}</a>}
            {address(c) && <div className="text-muted-foreground">{address(c)}</div>}
            <div className="text-xs text-muted-foreground">
              {[c.operation_type, c.home_state, (c.vehicles || []).join(", ")].filter(Boolean).join(" · ")}
            </div>
            <div className="text-xs">Plan: {c.plans.length ? c.plans.join(", ").replace(/cl_/g, "") : "Free"}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-6 text-center text-muted-foreground brutal-border">No customers found.</div>}
      </div>
      <div className="hidden md:block brutal-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-customers-table">
          <thead className="bg-[#141414]">
            <tr>
              {["Customer", "Compliance", "Email", "Phone", "Address", "Business", "Operation", "Filings", "Plan"].map((h) => (
                <th key={h} className="text-left p-3 font-mono-label text-[10px] text-muted-foreground border-b border-[#262626] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.user_id} className="border-b border-[#262626] hover:bg-[#141414]" data-testid={`admin-row-${c.user_id}`}>
                <td className="p-3 whitespace-nowrap">{c.name || "—"}</td>
                <td className="p-3 whitespace-nowrap">
                  <div className="flex flex-col items-start gap-2">
                    <ProgressCell pct={c.pct} tid={`admin-progress-${c.user_id}`} />
                    <NudgeButton c={c} api={api} onSent={onSent} />
                  </div>
                </td>
                <td className="p-3 whitespace-nowrap">{c.email ? <a href={`mailto:${c.email}`} className="underline">{c.email}</a> : "—"}</td>
                <td className="p-3 whitespace-nowrap">{c.phone ? <a href={`tel:${c.phone}`} className="underline">{c.phone}</a> : "—"}</td>
                <td className="p-3 min-w-[200px]">{address(c) || "—"}</td>
                <td className="p-3 min-w-[180px]">
                  <div>{c.legal_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{[c.dba_name, c.entity_type].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="p-3 whitespace-nowrap text-xs">
                  {c.operation_type || "—"}{c.home_state ? ` · ${c.home_state}` : ""}
                  <div className="text-muted-foreground">{(c.vehicles || []).join(", ")}</div>
                </td>
                <td className="p-3 whitespace-nowrap font-mono-label text-[11px]" data-testid={`admin-filings-${c.user_id}`} title="Completed / filings that apply to this client">{fil(c)}</td>
                <td className="p-3 whitespace-nowrap text-xs">{c.plans.length ? c.plans.join(", ").replace(/cl_/g, "") : "Free"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No customers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}
